import i18n from "@/i18n";
import { API_NUMBER_DATA_TYPES } from "@/lib/dynamicForms/constants/api-number-data-type.constant";
import { FIELD_TYPES } from "@/lib/dynamicForms/constants/dynamicForm.constant";
import type {
	CheckboxField,
	FileUploadField,
	FormField,
	TextField,
	UrlField,
} from "@/lib/dynamicForms/interfaces/dynamicForm.interface";
import { createFormField } from "@/lib/dynamicForms/utils/fieldFactory.util";
import { pickAdminFormPaths } from "../admin-form-editor-i18n-paths";
import type { FormEditorEntity } from "../form-editor-entity.types";
import type { BuilderFieldData, PaletteFieldType } from "../types";
import {
	PALETTE_EMAIL_PATTERN,
	PALETTE_URL_PATTERN,
} from "./palette-field-patterns";

/**
 * Palette types that still use interim projections: `email`, `textarea` as `text`.
 * `url` maps to {@link FIELD_TYPES.URL}. File maps to {@link FIELD_TYPES.FILE_UPLOAD}.
 */

export interface PaletteFieldIds {
	id: string;
	name: string;
	label: string;
}

/**
 * Builds a template {@link FormField} for a palette type using {@link createFormField}.
 * Interim palette-only types map to the closest lib type plus documented overrides.
 */
export function createPaletteTemplateField(
	paletteType: PaletteFieldType,
	ids: PaletteFieldIds,
	formEditorEntity: FormEditorEntity,
): FormField {
	const p = pickAdminFormPaths(formEditorEntity);
	const opts = { id: ids.id, name: ids.name, label: ids.label };

	switch (paletteType) {
		case "text":
			return createFormField(FIELD_TYPES.TEXT, opts);
		case "textarea": {
			const f = createFormField(FIELD_TYPES.TEXT, opts) as TextField;
			return {
				...f,
				validation: {
					...f.validation,
					rows: 3,
				},
			};
		}
		case "email": {
			const f = createFormField(FIELD_TYPES.TEXT, opts) as TextField;
			return {
				...f,
				validation: {
					pattern: PALETTE_EMAIL_PATTERN,
					custom: String(i18n.t(p.formEditor.validation.invalidEmail as never)),
				},
			};
		}
		case "url": {
			const f = createFormField(FIELD_TYPES.URL, opts) as UrlField;
			return {
				...f,
				placeholder:
					f.placeholder ??
					String(i18n.t(p.formEditor.defaults.urlPlaceholder as never)),
				validation: {
					pattern: PALETTE_URL_PATTERN,
					custom: String(i18n.t(p.formEditor.validation.invalidUrl as never)),
				},
			};
		}
		case "file":
			return createFormField(FIELD_TYPES.FILE_UPLOAD, opts);
		case "number":
			return createFormField(FIELD_TYPES.NUMBER, opts);
		case "phone":
			return createFormField(FIELD_TYPES.TELEPHONE, opts);
		case "date":
			return createFormField(FIELD_TYPES.DATE_PICKER, opts);
		case "time":
			return createFormField(FIELD_TYPES.TIME_PICKER, opts);
		case "dateTime":
			return createFormField(FIELD_TYPES.DATETIME_PICKER, opts);
		case "select":
			return createFormField(FIELD_TYPES.SELECT, opts);
		case "radio":
			return createFormField(FIELD_TYPES.RADIO, opts);
		case "checkbox":
			return createFormField(FIELD_TYPES.CHECKBOX, opts) as CheckboxField;
		case "multiSelect":
			return createFormField(FIELD_TYPES.MULTI_SELECT, opts);
		case "users":
			return createFormField(FIELD_TYPES.USERS, opts);
		case "signature":
			return createFormField(FIELD_TYPES.SIGNATURE, opts);
		case "address":
			return createFormField(FIELD_TYPES.ADDRESS, opts);
		default: {
			const _exhaustive: never = paletteType;
			return _exhaustive;
		}
	}
}

/** Typed option text. A blank label keeps the existing stored value. */
export function singleChoiceOption(option: {
	value: string;
	label: string;
}): { value: string; label: string } {
	const text = option.label.trim() !== "" ? option.label : option.value;
	return { value: text, label: text };
}

function fieldOptions(field: BuilderFieldData) {
	return field.options.map((option) => singleChoiceOption(option));
}

/**
 * Projects persisted builder data onto a palette template field for preview / persistence.
 */
export function projectBuilderFieldToFormField(
	field: BuilderFieldData,
	previewDisabled?: boolean,
	formEditorEntity: FormEditorEntity = "ticket",
): FormField {
	const template = createPaletteTemplateField(
		field.fieldType,
		{
			id: field.id,
			name: field.name,
			label: field.label,
		},
		formEditorEntity,
	);
	return mergeBuilderOntoTemplate(
		template,
		field,
		previewDisabled,
		formEditorEntity,
	);
}

function mergeBuilderOntoTemplate(
	template: FormField,
	field: BuilderFieldData,
	disabled: boolean | undefined,
	formEditorEntity: FormEditorEntity,
): FormField {
	const p = pickAdminFormPaths(formEditorEntity);
	const base = {
		id: field.id,
		name: field.name,
		label: field.label,
		required: field.required,
		disabled: disabled ?? false,
		hint: field.helpText || undefined,
	};

	switch (field.fieldType) {
		case "text":
			if (template.type !== "text") throw new Error("Expected text template");
			return {
				...template,
				...base,
				placeholder: field.placeholder ?? template.placeholder,
			};
		case "textarea":
			if (template.type !== "text") throw new Error("Expected text template");
			return {
				...template,
				...base,
				placeholder: field.placeholder ?? template.placeholder,
				validation: {
					...(template as TextField).validation,
					rows: field.textAreaRows ?? 3,
				},
			};
		case "email":
			if (template.type !== "text") throw new Error("Expected text template");
			return {
				...template,
				...base,
				placeholder: field.placeholder ?? template.placeholder,
			};
		case "url":
			if (template.type !== "url") throw new Error("Expected url template");
			return {
				...template,
				...base,
				placeholder:
					field.placeholder ??
					template.placeholder ??
					String(i18n.t(p.formEditor.defaults.urlPlaceholder as never)),
			};
		case "file": {
			if (template.type !== "file") {
				throw new Error("Expected file template");
			}
			const t0 = template as FileUploadField;
			const maxFromBuilder =
				field.fileMaxFiles != null &&
				Number.isFinite(field.fileMaxFiles) &&
				field.fileMaxFiles >= 1
					? Math.floor(field.fileMaxFiles)
					: undefined;
			const validation: NonNullable<FileUploadField["validation"]> = {
				...t0.validation,
			};
			if (maxFromBuilder !== undefined) {
				validation.maxFiles = maxFromBuilder;
			} else {
				delete validation.maxFiles;
			}
			const hasValidation = Object.keys(validation).length > 0;
			const presentation =
				field.filePresentation !== undefined
					? field.filePresentation
					: t0.presentation === "button"
						? "button"
						: "dropZone";
			return {
				...t0,
				...base,
				presentation,
				hint: field.helpText ?? t0.hint,
				dropZoneHint:
					field.placeholder !== undefined && field.placeholder !== ""
						? field.placeholder
						: t0.dropZoneHint,
				accept: field.fileAccept ?? t0.accept,
				multiple: field.fileMultiple !== false,
				validation: hasValidation ? validation : undefined,
			};
		}
		case "number":
			if (template.type !== "number")
				throw new Error("Expected number template");
			return {
				...template,
				...base,
				apiNumberDataType:
					field.numberApiDataType ?? template.apiNumberDataType,
				placeholder: field.placeholder ?? template.placeholder,
				validation: undefined,
			};
		case "phone":
			if (template.type !== "phone") throw new Error("Expected phone template");
			return {
				...template,
				...base,
				placeholder: field.placeholder ?? template.placeholder,
			};
		case "date":
			if (template.type !== "date") throw new Error("Expected date template");
			return {
				...template,
				...base,
				placeholder: field.placeholder ?? template.placeholder,
			};
		case "time":
			if (template.type !== "time") throw new Error("Expected time template");
			return {
				...template,
				...base,
				placeholder: field.placeholder ?? template.placeholder,
			};
		case "dateTime":
			if (template.type !== "dateTime")
				throw new Error("Expected dateTime template");
			return {
				...template,
				...base,
				placeholder: field.placeholder ?? template.placeholder,
			};
		case "select":
			if (template.type !== "select")
				throw new Error("Expected select template");
			return {
				...template,
				...base,
				placeholder: field.placeholder ?? template.placeholder,
				options: fieldOptions(field),
			};
		case "radio":
			if (template.type !== "radio") throw new Error("Expected radio template");
			return {
				...template,
				...base,
				options: fieldOptions(field),
			};
		case "checkbox":
			if (template.type !== "checkbox")
				throw new Error("Expected checkbox template");
			return {
				...template,
				...base,
			};
		case "multiSelect":
			if (template.type !== "multiSelect") {
				throw new Error("Expected multiSelect template");
			}
			return {
				...template,
				...base,
				placeholder: field.placeholder ?? template.placeholder,
				options: fieldOptions(field),
			};
		case "users":
			if (template.type !== "users") throw new Error("Expected users template");
			return {
				...template,
				...base,
				placeholder: field.placeholder ?? template.placeholder,
			};
		case "signature":
			if (template.type !== "signature") {
				throw new Error("Expected signature template");
			}
			return { ...template, ...base };
		case "address":
			if (template.type !== "address") {
				throw new Error("Expected address template");
			}
			return {
				...template,
				...base,
				placeholder: field.placeholder ?? template.placeholder,
			};
		default: {
			const _exhaustive: never = field.fieldType;
			return _exhaustive;
		}
	}
}

/**
 * Converts a palette template field into {@link BuilderFieldData} for new fields from the palette.
 */
export function builderFieldDataFromTemplate(
	paletteType: PaletteFieldType,
	template: FormField,
): BuilderFieldData {
	if (paletteType === "file" && template.type === "file") {
		const fu = template as FileUploadField;
		return {
			id: template.id,
			name: template.name,
			label: template.label,
			fieldType: "file",
			required: template.required ?? false,
			placeholder: fu.dropZoneHint,
			helpText: fu.hint,
			options: [],
			fileAccept: fu.accept,
			fileMultiple: fu.multiple !== false,
			fileMaxFiles: fu.validation?.maxFiles,
			filePresentation: fu.presentation === "button" ? "button" : undefined,
		};
	}

	const templateOptions =
		template.type === "select" ||
		template.type === "radio" ||
		template.type === "toggleButton" ||
		template.type === "multiSelect"
			? (template as { options: { value: string; label: string }[] }).options
			: [];
	const options =
		paletteType === "select" ||
		paletteType === "radio" ||
		paletteType === "multiSelect"
			? templateOptions.map((option) => singleChoiceOption(option))
			: templateOptions.map((option) => ({
					value: option.value,
					label: option.label,
				}));

	if (paletteType === "number" && template.type === "number") {
		return {
			id: template.id,
			name: template.name,
			label: template.label,
			fieldType: paletteType,
			numberApiDataType:
				template.apiNumberDataType ?? API_NUMBER_DATA_TYPES.Decimal,
			required: template.required ?? false,
			placeholder:
				template.placeholder !== undefined ? template.placeholder : undefined,
			helpText: template.hint,
			options: [],
		};
	}

	if (paletteType === "textarea" && template.type === "text") {
		const tf = template as TextField;
		return {
			id: template.id,
			name: template.name,
			label: template.label,
			fieldType: "textarea",
			required: template.required ?? false,
			placeholder:
				template.placeholder !== undefined ? template.placeholder : undefined,
			textAreaRows: tf.validation?.rows ?? 3,
			helpText: template.hint,
			options: [],
		};
	}

	return {
		id: template.id,
		name: template.name,
		label: template.label,
		fieldType: paletteType,
		required: template.required ?? false,
		placeholder:
			"placeholder" in template && template.placeholder !== undefined
				? template.placeholder
				: undefined,
		helpText: template.hint,
		options,
	};
}
